/* eslint-disable @typescript-eslint/no-floating-promises */
import React, {
  useState, useMemo, useCallback,
} from "react";

import { Trans, useTranslation } from "react-i18next";
import { EnergyTable } from "./EnergyTable";
import { DisplayChart } from "./DisplayChart";
import { Requester } from "../utils/Requester";
import {
  generateQueryParamString,
  TWO_WEEK_MILLISECONDS,
} from "../hooks/UseChartGetParams";
import { SensorCheckboxContainer } from "./SensorCheckboxContainer";

import style from "./SensorData.module.scss";
import { useChart } from "../contexts/ChartDataContext";
import { GenerateReport } from "../components/Misc/GenerateReport";
import { ExpandableButtonContainer } from "./ExpandableButtonContainer";
import { ChartConfigurator } from "../components/ChartTypePicker/ChartConfigurator";
import { InfoText } from "../components/Misc/InfoText";
import { Button } from "../components/Misc/Buttons/Button";
import { ContentBox } from "../components/Misc/ContentBox";
import { TabsContainer } from "../components/Misc/TabsNavigation/TabsContainer";
import { TabButton } from "../components/Misc/TabsNavigation/TabButton";
import { cleanUpDateTime } from "../utils/utility";
import { useHistory, useLocation } from "react-router-dom";
import { DateRangePicker } from "../components/DatePicker/DateRangePicker";
import { SimpleSelect } from "../components/Misc/SimpleSelect";
import { useChartDataType } from "../hooks/UseChartDataType";
import { FilterBlock } from "../components/Table/FilterBlock";


export function ChartDataManager(): React.ReactElement {
  const chart = useChart();
  const { t } = useTranslation();
  const location= useLocation();
  const history = useHistory();

  const updateDefaultSensors = () => {
    Requester.postDefaultSelectedSensors(chart.chartParams.nodes || {});
  };
  const [allDataTab, setAllDataTab] = useState(true);
  const [chartLoading, setChartLoading] = useState<boolean>(true);

  const fullrange = (chart.chartParams.x1 <= chart.availableDataRange.start
    && chart.chartParams.x2 >= chart.availableDataRange.end);

  const reportGeneratorQuery = useMemo(
    () =>
      generateQueryParamString(
        chart.chartParams,
        true,
        true,
      ),
    [chart.chartParams],
  );

  const handleChartLoading = useCallback((val: boolean) => {
    setChartLoading(val);
  }, [])

  const [,, chartTypeOptions] = useChartDataType(chart.chartParams.chartType);

  return (
    <>
      <div className={style["chart-view"]}>
        <DisplayChart
          selSensorTypes={chart.selSensorTypes}
          secondaryAxis={chart.secondaryAxis}
          forcedTimezone={chart.chartParams.timezone}
          nodes={chart.nodes}
          noNodesAvailable={chart.noNodesAvailable}
          startDate={chart.chartParams.x1}
          endDate={chart.chartParams.x2}
          chartType={chart.chartParams.chartType}
          loading={chartLoading}
          setLoading={handleChartLoading}
          yMaxMain={chart.yMaxMain}
          yMinMain={chart.yMinMain}
          yMaxSecond={chart.yMaxSecond}
          yMinSecond={chart.yMinSecond}
          mcList={chart.materialMap}
        />
        <div className={style.sidebar}>
          <FilterBlock label={t("Chart Type")}>
            <SimpleSelect
              selectedOption={chartTypeOptions.find(option => chart.chartParams.chartType === option.valueString)}
              options={chartTypeOptions}
              getOptionLabel={option => option.displayString}
              onChange={option => chart.updateChartType(option.valueString)}
            />
          </FilterBlock>
          <DateRangePicker
            start={chart.chartParams.visibleDates ? chart.chartParams.x1 : undefined}
            end={chart.chartParams.visibleDates ? chart.chartParams.x2 : undefined}
            max={new Date()}
            onRangeSelect={(start, end) => start && end && chart.loadDataRange(start, end, false, true)}
          />
          <hr style={{margin: "0px"}}/>
          <Button
            onClick={() => {
              setChartLoading(true);
              if (chart.chartParams.allData) {
                chart.loadDataRange(
                  new Date(new Date().getTime() - TWO_WEEK_MILLISECONDS),
                  new Date(),
                  false,
                  false,
                );
              } else {
                chart.loadDataRange(
                  chart.availableDataRange.start,
                  chart.availableDataRange.end,
                  true,
                  false,
                );
              }
            }}
          >
            {chart.chartParams.allData
              ? t("Load 2 week data")
              : t("Load all data")}
          </Button>
          <ExpandableButtonContainer 
            title={t("Configure Chart")}
          >
            <ChartConfigurator
              selSensorTypes={chart.selSensorTypes}
              secondaryAxis={chart.secondaryAxis}
              yMaxMain={chart.yMaxMain}
              yMinMain={chart.yMinMain}
              yMaxSecond={chart.yMaxSecond}
              yMinSecond={chart.yMinSecond}
              updateChartConfig={chart.updateChartConfig}
            />
          </ExpandableButtonContainer>
          <Button
            onClick={updateDefaultSensors}
          >
            {t("Save Default Chart")}
          </Button>
          <ExpandableButtonContainer
            title={t("Generate Report")}
          > 
            <p className="mt-4 mb-4 mr-2 ml-2">
              <Trans>
                Report will be created reflecting the currently visible
                data range and the selected sensors.
              </Trans>
              <br/>
            </p>
            {chart.hasData && !fullrange && (
              <InfoText>
                {t("Selected date range doesn't cover entire runtime")}
              </InfoText>
            )}
            {chart.hasData ||
              <InfoText>
                {t("Selected data range must contain data to generate a report")}
              </InfoText>
            }
            <GenerateReport
              queryContainer={[
                {
                  installation:
                    chart.installation.pk.toString(),
                  chartParams: [reportGeneratorQuery],
                  materials: Object.fromEntries(chart.materialMap),
                },
              ]}
              hasData={chart.hasData}
            />
            <button
              className="mt-3"
              onClick={() => {
                const parts = location.pathname.split('/');
                parts[parts.length - 1] = "report-config";
                history.push(parts.join("/"));
              }}
            >
              {t("Go to Report Configurations tab")}
            </button>
          </ExpandableButtonContainer>
          <ExpandableButtonContainer
            title={t("Select/Deselect Type")}
          >
            <SensorCheckboxContainer
              nodes={chart.nodes}
              sensorToggle={chart.toggleAllSensorTypes}
              />
          </ExpandableButtonContainer>
        </div>
      </div>
      <br />
      {chart.energyData.length > 0 &&
        <>
          <TabsContainer>
            <TabButton
              title={t("All time")}
              selected={allDataTab}
              onClick={() => setAllDataTab(true)}
            />
            <TabButton
              title={`${cleanUpDateTime(chart.chartParams.x1.toISOString())} -${" "}
              ${cleanUpDateTime(chart.chartParams.x2.toISOString())} ${t("range")}`}
              selected={!allDataTab}
              onClick={() => setAllDataTab(false)}
            />
          </TabsContainer>
          <ContentBox>
            <EnergyTable
              co2={chart.installation.co2}
              tableData={allDataTab ? chart.allEnergyData : chart.energyData}
            />
          </ContentBox>
        </>
      }
    </>
  );
}
